import {ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, EMPTY, merge, Observable, of} from 'rxjs';
import {catchError, filter, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../../../destroyable.component';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {FormOutcome, Task} from '../../../../shared/workflow/workflow-v2.interface';
import {WorkflowV2Service} from '../../../../shared/workflow/workflow-v2.service';
import {TaskWithFormModel} from '../../services/task.resolver';
import {TasksService} from '../../services/tasks.service';

@Component({
  selector: 'gp-task-view',
  templateUrl: './task-view.component.html',
  styleUrls: ['./task-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskViewComponent extends Destroyable implements OnInit, OnDestroy {

  public taskWithFormModel$: Observable<TaskWithFormModel>;

  public task: Task;

  private formOutcomeSubject$ = new BehaviorSubject<FormOutcome>(null);
  public trigger$: Observable<number>;

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private workflowV2Service: WorkflowV2Service,
              private toastrService: ToastrService,
              private translate: TranslateService,
              private tasksService: TasksService,
              private localization: LocalizationService) {
    super();
    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
  }

  ngOnInit(): void {
     this.activatedRoute.params
      .pipe(
        tap((params: Params) => this.tasksService.taskIdLastViewed$.next(params.taskId)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.taskWithFormModel$ = this.activatedRoute.data
      .pipe(
        filter(({taskWithFormModel}) => !!taskWithFormModel),
        map(({taskWithFormModel}) => taskWithFormModel),
        switchMap((taskWithFormModel: TaskWithFormModel) => {

          if (!taskWithFormModel.task.read) {
            return this.tasksService.updateTaskReadStatus(taskWithFormModel.task.id, true).pipe(
              map(() => ({...taskWithFormModel, task: {...taskWithFormModel.task, read: true}}))
            );
          }
          return of(taskWithFormModel);
        }),
        tap((taskWithFormModel) => this.task = taskWithFormModel.task),
        takeUntil(this.componentDestroyed$)
      );

    this.formOutcomeSubject$.pipe(
      filter((formOutcome) => formOutcome !== null),
      switchMap((formOutcome) => {
        if (formOutcome.updatedRequest) {
          return this.tasksService.updateTaskRequestVariable(this.task, formOutcome);
        }
        return of(formOutcome);
      }),
      switchMap((formOutcome) =>
        this.taskWithFormModel$.pipe(
          switchMap(({task, taskFormModel}) =>
            this.workflowV2Service.completeTaskWithForm(task.id, taskFormModel, formOutcome)
              .pipe(
                tap(() => {
                  this.toastrService.success(this.translate.instant('WORKFLOWS.task_completed'));
                  this.onClose();
                }),
                catchError(() => EMPTY)
              )))
      ),
      takeUntil(this.componentDestroyed$)).subscribe();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.tasksService.taskIdLastViewed$.next(null);
  }

  public onClose(): void {
    this.router.navigate(['/inbox/tasks'], {queryParamsHandling: 'merge'});
    this.tasksService.taskIdLastViewed$.next(null);
  }

  public onCompleteTaskWithForm(formOutcome: FormOutcome) {
    this.formOutcomeSubject$.next(formOutcome);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler() {
    this.onClose();
  }
}
